import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import { useAppTheme } from "@/hooks/useAppTheme";
import { useChartPalette } from "../utils/chartUtils";
import { getChartTooltipStyle, getCartesianGridProps } from "../utils/chartStyles";
import type { VendorPairingPoint } from "@/types";

interface VendorRelationshipChartProps {
  data: VendorPairingPoint[];
}

/**
 * Stacked horizontal bar chart showing each carrier's TPA composition.
 * Each carrier bar is broken into segments by TPA, showing the relationship landscape.
 */
export const VendorRelationshipChart = ({ data }: VendorRelationshipChartProps) => {
  const { theme } = useAppTheme();
  const chartPalette = useChartPalette();

  const { chartData, tpas } = useMemo(() => {
    if (data.length === 0) return { chartData: [], tpas: [] };

    const tpaSet = new Set<string>();
    const carrierMap = new Map<string, Record<string, number>>();

    for (const d of data) {
      tpaSet.add(d.tpa_name);
      if (!carrierMap.has(d.carrier_name)) carrierMap.set(d.carrier_name, {});
      carrierMap.get(d.carrier_name)![d.tpa_name] = (carrierMap.get(d.carrier_name)![d.tpa_name] ?? 0) + d.policy_count;
    }

    const tpas = Array.from(tpaSet).sort();

    const chartData = Array.from(carrierMap.entries())
      .map(([carrier, tpaCounts]) => ({
        carrier,
        ...Object.fromEntries(tpas.map((t) => [t, tpaCounts[t] ?? 0])),
      }))
      .sort((a, b) => {
        const totalA = tpas.reduce((sum, t) => sum + ((a[t] as number) ?? 0), 0);
        const totalB = tpas.reduce((sum, t) => sum + ((b[t] as number) ?? 0), 0);
        return totalB - totalA;
      });

    return { chartData, tpas };
  }, [data]);

  if (chartData.length === 0) return <EmptyChartState />;

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
        <CartesianGrid {...getCartesianGridProps(theme)} horizontal={false} vertical />
        <XAxis type="number" tickLine={false} axisLine={false} tick={{ fontSize: 10 }} allowDecimals={false} />
        <YAxis
          type="category"
          dataKey="carrier"
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 10 }}
          width={100}
        />
        <Tooltip contentStyle={getChartTooltipStyle(theme)} />
        {tpas.map((tpa, i) => (
          <Bar key={tpa} dataKey={tpa} stackId="tpa" fill={chartPalette[i % chartPalette.length]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};
